// External deps
import { useRef, useState } from 'react';
import { PropTypes } from 'prop-types';
import { ModalGeneric, Button, Loader, ContentCard, Label } from 'ypf-pas-general-ui-components';
import * as Yup from 'yup';
import { Formik } from 'formik';

// Internal deps
import { ubicationTypeEnum } from '../../../../utils/dataStatus';
import useToast from '../../../../hooks/useToast';
import { checkRol } from '../../../../security';
import { proveedorNotificador } from '../../../../security/constantsRols';
import { initialValuesAddUbication } from '../../../CreateServiceRequest/Constants';
import validationSchemaAddLocation from '../../../CreateServiceRequest/Schemas';
import AccountAssignmentObject from '../../../AccountAssignmentObject/AccountAssignmentObject';
import { getUbicationLabel } from '../../../../utils/getUbicationLabel';

import LocationForm from './LocationForm/LocationForm';

export const validationSchema = Yup.object().shape({
  ubicationType: Yup.object().nullable().required('Seleccione tipo de ubicación'),
  uunn: Yup.object().nullable().required('Seleccione unidad de negocio'),
  ubication: Yup.object().nullable().required('Ingrese ubicación'),
  classification: Yup.object().nullable(),
  event: Yup.object().nullable(),
  team: Yup.object().nullable(),
  imputationObject: Yup.object().shape({
    wells: Yup.array().of(
      Yup.object().shape({
        well: Yup.object()
          .test('wellDuplicate', 'El pozo ya fue agregado', (item, data) => {
            const wellsArray = data?.from[3]?.value?.imputationObject?.wells;
            let error = true;
            const indexInput = Number(data.path.split('[')[1][0]);

            wellsArray?.forEach((well, i) => {
              if (well?.well?.label === item?.label && i !== indexInput) {
                error = false;
              }
            });

            return error;
          })
          .nullable()
          .required('Ingrese un pozo'),
        event: Yup.lazy((value) => {
          if (!value) return Yup.mixed().notRequired();

          return Yup.object()
            .test('eventDiff', 'Ingrese el mismo evento para todos los pozos', (item, data) => {
              const wellsArray = data?.from[3]?.value?.imputationObject?.wells;
              let error = true;
              const indexInput = Number(data.path.split('[')[1][0]);
              // The id belongs to the event
              // map so that value is now wellEventId
              // (which is specific to the Event–Well relationship)
              // wellEventId and event id are always different
              const eventDiff = wellsArray?.some(
                (well, i) => well?.event?.id !== item?.id && i !== indexInput
              );

              if (item?.id) {
                if (eventDiff) {
                  error = false;
                }
              }

              return error;
            })
            .nullable()
            .required('Seleccione un evento');
        }),
        team: Yup.lazy((value) =>
          !value
            ? Yup.mixed().notRequired()
            : Yup.object().nullable().required('Seleccione un equipo')
        ),
        imputationType: Yup.object().nullable().required('Seleccione un tipo de imputación'),
        imputationObject: Yup.object().nullable().required('Seleccione un objeto de imputación'),
        state: Yup.string().nullable(),
        budget: Yup.string().nullable(),
        logisticCenter: Yup.string().nullable(),
        socCode: Yup.string().nullable(),
        soc: Yup.array().of(Yup.string()).nullable(),
        add: Yup.bool(),
        editable: Yup.bool()
      })
    )
  })
});

const ModalAddLocation = ({
  show,
  onClose,
  params,
  canAddWellToImputationObject,
  canEditImputationObject,
  notFilterTypeOptions,
  handleSubmit,
  isTemplate,
  eventos
}) => {
  const form = useRef(null);
  const [stepForm, setStepForm] = useState(1);
  const [loading, setLoading] = useState(false);
  const [maxWells, setMaxWells] = useState(1);

  const { setNewToast } = useToast();
  const isProvider = checkRol(proveedorNotificador);

  const onCloseModal = () => {
    form.current.setValues(initialValuesAddUbication);
    setStepForm(1);
    onClose();
  };

  const { isOyM } = form?.current?.values || false;

  const ubicationLabel = getUbicationLabel(!form.current?.values?.ubication?.ubicationType?.id);

  const checkValidStepOne = (values) => {
    if (values.ubication.ubicationType?.id === ubicationTypeEnum.locacion) {
      return !(values.ubication.uunn && values.ubication.ubication && values.ubication.typeEvent);
    }

    if (values.ubication.ubicationType?.id === ubicationTypeEnum.pozo) {
      return !(
        values.ubication.uunn &&
        values.ubication.ubication &&
        values.ubication.event &&
        values.ubication.team
      );
    }

    const isValidEquipment =
      values.ubication.equipment?.length <= 10 || !values.ubication.equipment;

    if (values.isOyM && values.ubication.ubicationType?.id !== ubicationTypeEnum.ducto) {
      return !(values.ubication?.uunn && values.ubication?.ubication && isValidEquipment);
    }

    return !(
      values.ubication?.uunn &&
      values.ubication?.ubication &&
      values.ubication?.installation &&
      isValidEquipment
    );
  };

  const onSubmitPropia = async ({ initForm = false }) => {
    setLoading(true);
    try {
      await handleSubmit(form.current?.values);
      if (initForm) {
        setStepForm(1);
        form.current.setValues(initialValuesAddUbication);
      } else {
        onCloseModal();
      }
      setNewToast({
        type: 'SUCCESS',
        message: 'La ubicación se agregó correctamente'
      });
    } catch (error) {
      setNewToast({
        type: 'ERROR',
        message: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalGeneric
      backdrop="static"
      infoModal
      onHide={() => {
        onCloseModal();
      }}
      show={show}
      size="xl"
      title="Agregar ubicación"
    >
      <Formik
        initialValues={initialValuesAddUbication}
        innerRef={form}
        validationSchema={validationSchemaAddLocation}
      >
        {({ values }) => (
          <>
            {loading && <Loader show text="CARGANDO ..." />}
            {stepForm === 1 && (
              <LocationForm
                createSDS
                eventos={eventos}
                isTemplate={isTemplate}
                notFilterTypeOptions={notFilterTypeOptions}
                params={params}
                setMaxWells={setMaxWells}
              />
            )}
            {stepForm === 2 && (
              <>
                <ContentCard className="mb-4 p-3 d-flex" orientation="horizontal">
                  <Label
                    label="Unidad de Negocio/Operación"
                    value={values.ubication.uunn?.label || '-'}
                  />
                  <Label
                    label="Tipo de ubicación"
                    value={values.ubication.ubicationType?.label || '-'}
                  />
                  <Label label={ubicationLabel} value={values.ubication.ubication?.label || '-'} />
                </ContentCard>
                <AccountAssignmentObject
                  bussinesUnitId={values.ubication.uunn?.id}
                  createSDS
                  disabledAddWell={!canAddWellToImputationObject}
                  disabledImputationForm={!canEditImputationObject}
                  eventId={values?.ubication?.event?.id}
                  imputationObjectDefault={isProvider}
                  locationId={values.ubication.ubication?.id}
                  maxCantWells={maxWells || 1}
                  showDetailWell
                  typeOfEventId={values?.ubication?.typeEvent?.id}
                  ubicationType={{
                    id: values.ubication?.ubicationType?.id
                  }}
                />
              </>
            )}
            <hr />
            <div className="d-flex justify-content-center">
              <Button
                className="mx-1"
                label="Salir"
                onClick={() => {
                  onCloseModal();
                }}
                type="button"
                variant="outline-primary"
              />
              {stepForm === 1 && !isOyM && (
                <Button
                  className="mx-1"
                  disabled={checkValidStepOne(values)}
                  label="Continuar"
                  onClick={() => {
                    setStepForm(2);
                  }}
                  type="button"
                  variant="primary"
                />
              )}

              {(stepForm === 2 || isOyM) && (
                <>
                  <Button
                    className="mx-1"
                    disabled={
                      values.imputationObject?.wells?.some((w) => w?.add) ||
                      !values.imputationObject?.wells?.length ||
                      (isOyM && checkValidStepOne(values))
                    }
                    label="Agregar"
                    onClick={() => {
                      onSubmitPropia({ initForm: false });
                    }}
                    type="button"
                    variant="primary"
                  />

                  <Button
                    className="mx-1"
                    disabled={
                      values.imputationObject?.wells?.some((w) => w?.add) ||
                      !values.imputationObject?.wells?.length ||
                      (isOyM && checkValidStepOne(values))
                    }
                    label="Agregar y continuar"
                    onClick={() => {
                      onSubmitPropia({ initForm: true });
                    }}
                    type="button"
                    variant="primary"
                    widthButton="160px"
                  />
                </>
              )}
            </div>
          </>
        )}
      </Formik>
    </ModalGeneric>
  );
};

ModalAddLocation.propTypes = {
  show: PropTypes.bool,
  isTemplate: PropTypes.bool.isRequired,
  canAddWellToImputationObject: PropTypes.bool.isRequired,
  canEditImputationObject: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  params: PropTypes.shape({
    optionsUbicationType: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        nombre: PropTypes.string
      })
    ),
    optionsBusinessOptions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        nombre: PropTypes.string
      })
    ),
    optionsOperations: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        nombre: PropTypes.string
      })
    ),
    filteredOperations: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        nombre: PropTypes.string
      })
    ),
    filteredBusinessUnits: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        nombre: PropTypes.string
      })
    ),
    disabledChangeUunn: PropTypes.bool
  }),
  notFilterTypeOptions: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  eventos: PropTypes.arrayOf(PropTypes.shape)
};
ModalAddLocation.defaultProps = {
  show: false,
  onClose: () => {},
  params: {
    optionsUbicationType: [],
    optionsBusinessOptions: [],
    optionsOperations: [],
    filteredOperations: [],
    filteredBusinessUnits: [],
    disabledChangeUunn: false
  },
  eventos: []
};
export default ModalAddLocation;
